import { useEffect, useState } from 'react';
import { useAccessToken } from '@app/auth';
import { ConversationDto, fetchConversations } from '../api/conversations';

export interface UseConversationsResult {
  conversations: ConversationDto[];
  loading: boolean;
  error: string | null;
  /** True when DB is unreachable and mock data is being used */
  usingFallback: boolean;
  refetch: () => void;
}

export function useConversations(): UseConversationsResult {
  const getToken = useAccessToken();
  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchConversations(getToken)
      .then((data) => {
        if (cancelled) return;
        setConversations(data);
        setUsingFallback(false);
      })
      .catch(() => {
        if (cancelled) return;
        // DB unavailable — caller falls back to MOCK_CONVERSATIONS
        setConversations([]);
        setUsingFallback(true);
        setError('Could not load conversations from server');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [getToken, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = () => setTick((t) => t + 1);

  return { conversations, loading, error, usingFallback, refetch };
}
